import type { Question } from '@shared/domain/form';
import type {
  BaseReview,
  ReviewStatus,
  ReviewStep,
} from '@shared/domain/review';
import { useContext, useEffect, useRef } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';
import { useExtendState } from '@shared/utils/ExtendState';
import { useReviewContext } from '../context/ReviewContext';
import useValidation from './useValidation';
import { useLegalCheck } from './useLegalCheck';
import type { LegalCheckFlag } from '../legal-check/types';

export interface ReviewFormConfig<T extends BaseReview> {
  reviewStep: ReviewStep;
  formFields: Question[];
  editMode?: boolean;
  defaultValues?: Partial<T> & { id?: number };
  onSubmit?: (formFields: T, reviewStep: ReviewStep, isDraft?: boolean) => void;
  onEditSuccess?: (updatedStatus?: ReviewStatus) => void;
  customValidationFields?: Record<string, any>;
  // Historical AI scanner flags keyed by field name. Surfaced inline under
  // each TEXT field in admin edit mode so the super-admin sees the same
  // observations the contributor saw at submission time.
  seedLegalCheckFlags?: Record<string, LegalCheckFlag[]>;
}

export function useReviewForm<T extends BaseReview>({
  reviewStep,
  formFields: questionFields,
  editMode = false,
  defaultValues,
  onSubmit,
  onEditSuccess,
  customValidationFields = {},
  seedLegalCheckFlags,
}: ReviewFormConfig<T>) {
  const api = useApi();
  const { user } = useContext(UserContext);
  const { currentReview } = useReviewContext();
  const hasEditedSuccessfully = useRef(false);
  const hasUnsavedChanges = useRef(false);
  const initialFormDataRef = useRef<T | null>(null);

  // Extract default values without the id field for form initialization
  const initialFromDefaults: Partial<T> =
    editMode && defaultValues
      ? (Object.fromEntries(
          Object.entries(defaultValues).filter(([k]) => k !== 'id')
        ) as Partial<T>)
      : {};

  // Initialize form fields
  const [formFields, extendFormFields, setFormFields] = useExtendState<T>(
    editMode && defaultValues
      ? (initialFromDefaults as T)
      : ((currentReview ?? {}) as T)
  );

  // Combine form fields with custom validation fields for validation
  const validationFields = { ...formFields, ...customValidationFields };
  const { errors, validateFields, validateField } = useValidation(
    validationFields,
    questionFields
  );

  const extendFormFieldsWithTracking = (
    newFields: Partial<T> | Record<string, any>
  ) => {
    extendFormFields(newFields as Partial<T>);
    hasUnsavedChanges.current = true;
  };

  // Standard form field change handler
  const handleFormFieldChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    extendFormFieldsWithTracking({ ...formFields, [name]: value });
    validateField(name, value);
  };

  const {
    legalCheckFlags,
    legalCheckInFlight,
    dismissLegalCheckFlag,
    getLegalCheckBindings,
  } = useLegalCheck({
    questionFields,
    seedFlags: seedLegalCheckFlags,
  });

  const handleSubmit = () => {
    const hasErrors = validateFields();
    if (hasErrors) {
      return;
    }

    onSubmit?.(formFields, reviewStep, true);
    hasUnsavedChanges.current = false;
    initialFormDataRef.current = { ...formFields };
  };

  // Handle form edit
  const handleEdit = async () => {
    const hasErrors = validateFields();
    if (hasErrors) {
      return;
    }

    const targetId =
      editMode && defaultValues?.id ? defaultValues.id : currentReview?.id;

    if (!targetId) {
      return;
    }

    const response = await api.patch(`/review/${targetId}/edit`, {
      reviewId: targetId,
      data: formFields,
      step: reviewStep,
      isAdmin: user?.isAdmin,
    });

    hasEditedSuccessfully.current = true;
    hasUnsavedChanges.current = false;
    initialFormDataRef.current = { ...formFields };

    // Pass the updated review status to the success callback
    const updatedStatus = response.reviewStatus;

    onEditSuccess?.(updatedStatus);
  };

  useEffect(() => {
    if (editMode && hasEditedSuccessfully.current) {
      return;
    }

    if (hasUnsavedChanges.current) {
      return;
    }

    if (editMode && defaultValues) {
      const next = Object.fromEntries(
        Object.entries(defaultValues).filter(([k]) => k !== 'id')
      ) as Partial<T>;
      setFormFields(next as T);
      initialFormDataRef.current = next as T;
    } else {
      setFormFields((currentReview ?? {}) as T);
      initialFormDataRef.current = (currentReview ?? {}) as T;
    }
  }, [currentReview, editMode, defaultValues, setFormFields]);

  useEffect(() => {
    hasEditedSuccessfully.current = false;
    hasUnsavedChanges.current = false;
  }, [defaultValues?.id, reviewStep]);

  return {
    formFields,
    extendFormFields: extendFormFieldsWithTracking,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    legalCheckFlags,
    legalCheckInFlight,
    dismissLegalCheckFlag,
    getLegalCheckBindings,
  };
}
